import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import moment from 'moment';
import { useSelector } from 'react-redux';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface ChartData {
  mese_rif: string;
  prezzo: number;
}

interface BillingChartProps {
  chartData: ChartData[];
  className?: string;
  loading?: boolean;
}

const BillingChart: React.FC<BillingChartProps> = ({ 
  chartData = [],
  className = '',
  loading = false
}) => {
  const theme = useSelector((state: any) => state.menu.theme);

  const colorsLight = ["#6EA8FE", "#FF9E85", "#FFC97F"];
  const colorsDark = ["#6EA8FE", "#FF9E85", "#FFD700"];

  const colors = theme === 'dark' ? colorsDark : colorsLight;
  const textColor = theme === 'dark' ? '#FFFFFF' : '#000000';

  const mesi = [
    "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
  ];

  // Processa i dati per avere una struttura coerente con le etichette mensili
  const formattedData = useMemo(() => {
    const dataMap: Record<string, Record<string, number>> = { "2026": {}, "2025": {}, "2024": {} };

    chartData.forEach(entry => {
      const anno = moment(entry.mese_rif).format('YYYY');
      const mese = moment(entry.mese_rif).month(); // Ottiene l'indice del mese (0-11)
      
      if (dataMap[anno]) {
        dataMap[anno][mesi[mese]] = entry.prezzo;
      }
    });

    return dataMap;
  }, [chartData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: {
          display: false,
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: textColor,
        }
      },
      y: {
        grid: {
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: textColor,
          callback: function(value: any) {
            return new Intl.NumberFormat('it-IT', {
              style: 'currency',
              currency: 'EUR',
              maximumFractionDigits: 0,
            }).format(value);
          }
        }
      }
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: textColor,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            return new Intl.NumberFormat('it-IT', {
              style: 'currency',
              currency: 'EUR',
            }).format(context.raw);
          }
        }
      }
    },
  };

  const years = ['2026', '2025', '2024'];

  const chartDatasets = {
    labels: mesi,
    datasets: years.map((year, index) => ({
      label: `Fatturato ${year}`,
      data: mesi.map(mese => formattedData[year]?.[mese] || 0),
      backgroundColor: colors[index],
      borderColor: colors[index],
      borderWidth: 1,
    }))
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className={`h-[400px] w-full ${className}`}>
      <Bar options={chartOptions} data={chartDatasets} />
    </div>
  );
};

export default BillingChart;
